import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import AdminLayout from '@/layouts/AdminLayout';
import { useAuthState } from '@/hooks/useAuthState';
import { UserService } from '@/services/userService';
import { DevelopmentService } from '@/services/developmentService';
import { useToast } from '@/hooks/use-toast';
import { getUserImageUrl } from '@/config/api';
import SubscriptionPlanBadge from '@/components/SubscriptionPlanBadge';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import {
  Users,
  Search,
  Plus,
  Edit,
  Trash2,
  Eye,
  ArrowLeft,
  Filter,
  MoreHorizontal,
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  XCircle,
  Clock
} from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface User {
  id: number;
  name: string;
  email: string;
  phone?: string;
  user_type: string;
  plan_id?: number;
  subscription_plan?: string;
  plan_name?: string;
  profile_photo?: string;
  created_at: string;
  is_active: boolean;
  approval_status?: string;
  partner_referral_token?: string | null;
}

const AdminUsers = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { toast } = useToast();
  const { isAuthenticated, userType, userName, userPhoto, userId, loading: authLoading } = useAuthState();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState(''); // Estado para o input
  const [filterProfile, setFilterProfile] = useState(
    () => searchParams.get('tipo') || 'all',
  );
  const [filterStatus, setFilterStatus] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [resortOwnerIds, setResortOwnerIds] = useState<number[] | null>(null);
  const [resortFilterName, setResortFilterName] = useState<string | null>(
    () => searchParams.get('resort'),
  );
  const developmentIdFilter = searchParams.get('developmentId');
  const planFilter = searchParams.get('plano');

  // Verificar se é admin
  useEffect(() => {
    if (!authLoading && (!isAuthenticated || userType !== 'admin')) {
      navigate('/admin/login');
    }
  }, [isAuthenticated, userType, authLoading, navigate]);

  useEffect(() => {
    const tipo = searchParams.get('tipo');
    const plano = searchParams.get('plano');
    if (tipo && ['owner', 'guest', 'all'].includes(tipo)) {
      setFilterProfile(tipo);
    } else if (plano === 'live' || plano === 'porto2life' || plano === 'freemium') {
      setFilterProfile('owner');
    }
    setResortFilterName(searchParams.get('resort'));

    const devId = searchParams.get('developmentId');
    if (devId) {
      DevelopmentService.getOwnerIdsForDevelopment(parseInt(devId, 10))
        .then((data) => {
          setResortOwnerIds(data.ownerIds);
          if (!searchParams.get('resort')) {
            setResortFilterName(data.developmentName);
          }
        })
        .catch(() => {
          setResortOwnerIds([]);
          toast({
            title: 'Erro',
            description: 'Não foi possível carregar o filtro do resort.',
            variant: 'destructive',
          });
        });
    } else {
      setResortOwnerIds(null);
    }
  }, [searchParams, toast]);

  useEffect(() => {
    if (developmentIdFilter && resortOwnerIds === null) {
      return;
    }
    loadUsers();
  }, [
    currentPage,
    itemsPerPage,
    searchTerm,
    filterProfile,
    filterStatus,
    resortOwnerIds,
    developmentIdFilter,
    planFilter,
  ]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      
      // Obter o total real de usuários no banco (sem limite)
      const totalCount = await UserService.getTotalUsersCount(true);
      
      // Carregar usuários do banco de dados (incluindo inativos)
      // Usar um limite alto para carregar todos os usuários, mas ainda aplicar paginação no frontend
      const allUsers = await UserService.getAllUsersWithInactive(10000, 0); // Aumentar limite para 10000
      
      // Aplicar filtros
      let filteredUsers = allUsers;
      
      if (searchTerm) {
        filteredUsers = filteredUsers.filter(user => 
          user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          user.email.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }
      
      if (filterProfile !== 'all') {
        filteredUsers = filteredUsers.filter(user => user.user_type === filterProfile);
      }
      
      if (filterStatus !== 'all') {
        if (filterStatus === 'active') {
          filteredUsers = filteredUsers.filter(user => Boolean(user.is_active) === true);
        } else if (filterStatus === 'inactive') {
          filteredUsers = filteredUsers.filter(user => Boolean(user.is_active) === false);
        }
      }

      if (resortOwnerIds !== null) {
        const idSet = new Set(resortOwnerIds);
        filteredUsers = filteredUsers.filter((user) => idSet.has(user.id));
      }

      if (planFilter === 'live') {
        filteredUsers = filteredUsers.filter(
          (user) =>
            user.subscription_plan === 'live' ||
            user.plan_id === 3,
        );
      } else if (planFilter === 'porto2life') {
        filteredUsers = filteredUsers.filter(
          (user) =>
            user.subscription_plan === 'porto2life' ||
            user.plan_id === 2,
        );
      } else if (planFilter === 'freemium' || planFilter === 'essencial') {
        filteredUsers = filteredUsers.filter(
          (user) =>
            user.subscription_plan === 'freemium' ||
            user.plan_id === 1 ||
            !user.plan_id,
        );
      }

      // Calcular paginação
      // Se houver filtros aplicados, usar o tamanho do array filtrado
      // Caso contrário, usar o total real do banco (que conta todos os usuários)
      let total: number;
      if (
        searchTerm ||
        filterProfile !== 'all' ||
        filterStatus !== 'all' ||
        resortOwnerIds !== null ||
        !!planFilter
      ) {
        // Com filtros, contar os usuários filtrados
        total = filteredUsers.length;
      } else {
        // Sem filtros, usar o total real do banco
        total = totalCount;
      }
      
      const startIndex = (currentPage - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      const paginatedUsers = filteredUsers.slice(startIndex, endIndex);

      setUsers(paginatedUsers);
      setTotalItems(total);
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar os usuários",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (items: number) => {
    setItemsPerPage(items);
    setCurrentPage(1);
  };

  const handleSearch = () => {
    setSearchTerm(searchInput);
    setCurrentPage(1);
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setSearchTerm('');
    setCurrentPage(1);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleEditUser = (userId: number) => {
    navigate(`/admin/usuarios/editar/${userId}`);
  };

  const handleToggleUserStatus = async (userId: number, currentStatus: boolean) => {
    const action = currentStatus ? 'desativar' : 'ativar';
    const actionPast = currentStatus ? 'desativado' : 'ativado';
    
    if (confirm(`Tem certeza que deseja ${action} este usuário?`)) {
      try {
        await UserService.toggleUserStatus(userId, !currentStatus);
        toast({
          title: "Sucesso",
          description: `Usuário ${actionPast} com sucesso!`,
        });
        loadUsers();
      } catch (error) {
        console.error(`Erro ao ${action} usuário:`, error);
        toast({
          title: "Erro",
          description: `Não foi possível ${action} o usuário`,
          variant: "destructive",
        });
      }
    }
  };

  const handleApproveUser = async (userId: number) => {
    try {
      await UserService.approveUser(userId);
      toast({
        title: "Sucesso",
        description: "Usuário aprovado com sucesso!",
      });
      loadUsers();
    } catch (error: any) {
      toast({
        title: "Erro",
        description: error.message || "Erro ao aprovar usuário",
        variant: "destructive",
      });
    }
  };

  const handleRejectUser = async (userId: number) => {
    if (window.confirm('Tem certeza que deseja rejeitar este usuário?')) {
      try {
        await UserService.rejectUser(userId);
        toast({
          title: "Sucesso",
          description: "Usuário rejeitado com sucesso!",
        });
        loadUsers();
      } catch (error: any) {
        toast({
          title: "Erro",
          description: error.message || "Erro ao rejeitar usuário",
          variant: "destructive",
        });
      }
    }
  };

  const handleViewUser = (userId: number) => {
    navigate(`/admin/usuarios/visualizar/${userId}`);
  };

  const getUserTypeLabel = (type: string) => {
    switch (type) {
      case 'admin': return 'Administrador';
      case 'owner': return 'Proprietário';
      case 'guest': return 'Hóspede';
      default: return type;
    }
  };

  const getApprovalStatusLabel = (status: string) => {
    switch (status) {
      case 'pending': return 'Pendente';
      case 'approved': return 'Aprovado';
      case 'rejected': return 'Rejeitado';
      default: return status;
    }
  };

  const getApprovalStatusBadge = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'approved': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getUserTypeColor = (type: string) => {
    switch (type) {
      case 'admin': return 'bg-red-100 text-red-800';
      case 'owner': return 'bg-blue-100 text-blue-800';
      case 'guest': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Carregando usuários...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="container mx-auto px-4 py-8">
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="outline" onClick={() => navigate('/admin/dashboard')}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Voltar
              </Button>
              <div>
                <h1 className="text-2xl font-bold">Gerenciar Usuários</h1>
                <p className="text-muted-foreground">
                  Cadastre, edite e gerencie usuários do sistema
                </p>
    </div>
    </div>
            <Button onClick={() => navigate('/admin/usuarios/novo')}>
              <Plus className="h-4 w-4 mr-2" />
              Novo Usuário
            </Button>
    </div>

          {planFilter === 'live' && !developmentIdFilter && (
            <Card className="mb-4 border-orange-200 bg-gradient-to-r from-orange-500/10 to-amber-500/10">
              <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <p className="text-sm">
                  Exibindo proprietários no <strong>plano Live</strong>
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setSearchParams({});
                    setFilterProfile('all');
                    setCurrentPage(1);
                  }}
                >
                  Limpar filtro
                </Button>
              </CardContent>
            </Card>
          )}

          {developmentIdFilter && resortFilterName && (
            <Card className="mb-4 border-primary/30 bg-primary/5">
              <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <p className="text-sm">
                  Exibindo proprietários com imóveis em{' '}
                  <strong>{resortFilterName}</strong>
                  {resortOwnerIds !== null && (
                    <span className="text-muted-foreground">
                      {' '}
                      ({resortOwnerIds.length}{' '}
                      {resortOwnerIds.length === 1 ? 'usuário' : 'usuários'})
                    </span>
                  )}
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setSearchParams({});
                    setFilterProfile('all');
                    setResortOwnerIds(null);
                    setResortFilterName(null);
                    setCurrentPage(1);
                  }}
                >
                  Limpar filtro do resort
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Filtros e Busca */}
          <Card>
            <CardContent className="p-6">
              <div className="flex flex-col lg:flex-row gap-4">
                <div className="flex-1">
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                      <Input
                        placeholder="Buscar por nome ou email..."
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        onKeyPress={handleKeyPress}
                        className="pl-10"
                      />
                    </div>
                    <Button 
                      onClick={handleSearch}
                      variant="outline"
                      className="px-4"
                    >
                      <Search className="h-4 w-4" />
                    </Button>
                    {searchTerm && (
                      <Button 
                        onClick={handleClearSearch}
                        variant="outline"
                        className="px-4"
                      >
                        Limpar
                      </Button>
                    )}
                  </div>
                  {searchTerm && (
                    <p className="text-sm text-muted-foreground mt-2">
                      Buscando por: <strong>"{searchTerm}"</strong>
                    </p>
                  )}
                </div>
                <div className="flex gap-4">
                  <div className="flex items-center gap-2">
                    <label className="text-sm font-medium text-gray-700">Perfil:</label>
                    <Select value={filterProfile} onValueChange={setFilterProfile}>
                      <SelectTrigger className="w-40">
                        <SelectValue placeholder="Selecione o perfil" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos</SelectItem>
                        <SelectItem value="owner">Proprietário</SelectItem>
                        <SelectItem value="guest">Hóspede</SelectItem>
                      </SelectContent>
                    </Select>
    </div>
                  
                  <div className="flex items-center gap-2">
                    <label className="text-sm font-medium text-gray-700">Status:</label>
                    <Select value={filterStatus} onValueChange={setFilterStatus}>
                      <SelectTrigger className="w-40">
                        <SelectValue placeholder="Selecione o status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos</SelectItem>
                        <SelectItem value="active">Ativo</SelectItem>
                        <SelectItem value="inactive">Inativo</SelectItem>
                      </SelectContent>
                    </Select>
    </div>
    </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">Itens por página:</span>
                  <Select value={itemsPerPage.toString()} onValueChange={(value) => handleItemsPerPageChange(parseInt(value))}>
                    <SelectTrigger className="w-20">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="10">10</SelectItem>
                      <SelectItem value="25">25</SelectItem>
                      <SelectItem value="50">50</SelectItem>
                      <SelectItem value="100">100</SelectItem>
                    </SelectContent>
                  </Select>
    </div>
    </div>
            </CardContent>
          </Card>

      {/* Tabela de Usuários */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Usuários ({totalItems} total)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nome</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>Plano</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Aprovação</TableHead>
                <TableHead>Token de Indicação</TableHead>
                <TableHead>Data de Cadastro</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.id}>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-3">
                      <Avatar className="w-10 h-10">
                        <AvatarImage 
                          src={user.profile_photo && user.id 
                            ? getUserImageUrl(user.id, user.profile_photo)
                            : undefined
                          }
                          alt={user.name}
                          className="object-cover"
                        />
                        <AvatarFallback className="bg-primary/10">
                          <Users className="h-5 w-5 text-primary" />
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <div className="font-medium">{user.name}</div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    <Badge className={getUserTypeColor(user.user_type)}>
                      {getUserTypeLabel(user.user_type)}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {user.user_type === 'owner' ? (
                      <SubscriptionPlanBadge
                        plan={user.subscription_plan}
                        planId={user.plan_id}
                        size="sm"
                      />
                    ) : (
                      <span className="text-sm text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant={user.is_active ? 'default' : 'secondary'}>
                      {user.is_active ? 'Ativo' : 'Inativo'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge className={getApprovalStatusBadge(user.approval_status || 'pending')}>
                      {getApprovalStatusLabel(user.approval_status || 'pending')}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {user.partner_referral_token ? (
                      <span className="font-mono text-sm bg-muted px-2 py-1 rounded">
                        {user.partner_referral_token}
                      </span>
                    ) : (
                      <span className="text-muted-foreground text-sm">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {new Date(user.created_at).toLocaleDateString('pt-BR')}
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleViewUser(user.id)}>
                          <Eye className="h-4 w-4 mr-2" />
                          Visualizar
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleEditUser(user.id)}>
                          <Edit className="h-4 w-4 mr-2" />
                          Editar
                        </DropdownMenuItem>
                        {user.user_type === 'owner' && (user.approval_status || 'pending') === 'pending' && (
                          <>
                            <DropdownMenuItem 
                              onClick={() => handleApproveUser(user.id)}
                              className="text-green-600"
                            >
                              <CheckCircle className="h-4 w-4 mr-2" />
                              Aprovar
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={() => handleRejectUser(user.id)}
                              className="text-red-600"
                            >
                              <XCircle className="h-4 w-4 mr-2" />
                              Rejeitar
                            </DropdownMenuItem>
                          </>
                        )}
                        <DropdownMenuItem 
                          onClick={() => handleToggleUserStatus(user.id, user.is_active)}
                          className={user.is_active ? "text-orange-600" : "text-green-600"}
                        >
                          {user.is_active ? (
                            <>
                              <Trash2 className="h-4 w-4 mr-2" />
                              Desativar
                            </>
                          ) : (
                            <>
                              <Users className="h-4 w-4 mr-2" />
                              Ativar
                            </>
                          )}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Paginação */}
      {totalPages > 1 && (
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="text-sm text-muted-foreground">
                Mostrando {((currentPage - 1) * itemsPerPage) + 1} a {Math.min(currentPage * itemsPerPage, totalItems)} de {totalItems} registros
    </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Anterior
                </Button>
                
                <div className="flex items-center gap-1">
                  {(() => {
                    const pages: (number | string)[] = [];
                    const maxVisible = 5; // Máximo de 5 botões visíveis
                    
                    if (totalPages <= maxVisible) {
                      // Se há 5 ou menos páginas, mostrar todas
                      for (let i = 1; i <= totalPages; i++) {
                        pages.push(i);
                      }
                    } else {
                      // Calcular range de páginas ao redor da atual
                      let startPage = Math.max(1, currentPage - 2);
                      let endPage = Math.min(totalPages, currentPage + 2);
                      
                      // Ajustar se estiver muito no início
                      if (currentPage <= 3) {
                        startPage = 1;
                        endPage = Math.min(5, totalPages);
                      }
                      
                      // Ajustar se estiver muito no final
                      if (currentPage >= totalPages - 2) {
                        startPage = Math.max(1, totalPages - 4);
                        endPage = totalPages;
                      }
                      
                      // Sempre mostrar primeira página se não estiver no range
                      if (startPage > 1) {
                        pages.push(1);
                        if (startPage > 2) {
                          pages.push('...');
                        }
                      }
                      
                      // Mostrar páginas do range
                      for (let i = startPage; i <= endPage; i++) {
                        pages.push(i);
                      }
                      
                      // Sempre mostrar última página se não estiver no range
                      if (endPage < totalPages) {
                        if (endPage < totalPages - 1) {
                          pages.push('...');
                        }
                        pages.push(totalPages);
                      }
                    }
                    
                    return pages.map((page, index) => {
                      if (page === '...') {
                        return (
                          <span key={`ellipsis-${index}`} className="px-2 text-muted-foreground">
                            ...
                          </span>
                        );
                      }
                      
                      return (
                        <Button
                          key={page}
                          variant={currentPage === page ? "default" : "outline"}
                          size="sm"
                          onClick={() => handlePageChange(page as number)}
                          className="w-8 h-8 p-0"
                        >
                          {page}
                        </Button>
                      );
                    });
                  })()}
                </div>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Próxima
                  <ChevronRight className="h-4 w-4" />
                </Button>
    </div>
    </div>
          </CardContent>
        </Card>
      )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminUsers;
